import axios from 'axios';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { checkStatusCamera, dayChooseByTimestamp, getDownloadAlert, getDownloadPoint } from '../../../api/openApi';
import { IS_MOBILE_APP, UserCan } from '../../../shared/constants';
import { clientStore, FieldValue } from '../../../shared/firebase';
import { alertDotIcon, clearTimezone, countDateWithUserAndTimezoneOffset } from '../../../shared/helpers';
import { HttpStatus, iDeviceDetails, iDevicePing, iFullStoreState, iPerson, Units } from '../../../shared/interfaces';
import { useRedux } from '../../../states/redux-state';
import { iReducersState } from '../../../stores/reducers';
import { hidePoint, removeReportTrips, showPoint } from '../../../stores/reducers/devicesTripsPoints/AC';
import { IReducerSelectedPoint } from '../../../stores/reducers/selectedPoint';
import { cameraAvailableTime, openModal, statusCameraOnline, takeDeviceInfo } from '../../../stores/reducers/videoCameras';
import { getTimezoneByValue } from '../../pages/devices/DeviceTabPageEdit/Timezones/Timezones.helper';

import { ReportAC } from '../../../stores/reducers/report-reducers';
import { getLast30DayDate, getStartAndEndOfDate } from '../../../utils';
import ModalImage from '../../ModalImage';
import AddAlarmIdModalForm from '../AddAlarmIdModalForm/AddAlarmIdModalForm';
import AddAlarmIdModalPrompt from '../AddAlarmIdModalPrompt/AddAlarmIdModalPrompt';
import styles from '../NewPointDetails.module.scss';
import { PointDetailsHeader } from './PointDetailsHeader';
import { PointDetailsInfo } from './PointDetailsInfo';
import { PointDetailsActions } from './PointDetailsActions';
import { PointDetailsStatusBar } from './PointDetailsStatusBar';
import { formatAddress, getFormattedMakeModel, isMoreThan30DaysAgo, PointAddress } from './PointDetails.helpers';

enum DownloadPointResponseStatuses {
    ADD_POINT = 'Add_point',
    UNKNOWN = 'Unknown',
    DOWNLOADING = 'Downloading',
    WAITING = 'Waiting',
    FAILURE = 'Failure',
    COMPLETED = 'Completed'
}

type VideoTypeName = 'Event' | 'Point';

enum VideoTypeNameEnum {
    EVENT = 'Event',
    POINT = 'Point'
}

type iPointDetails = {
    gotoStreet: () => void;
    point: iDevicePing;
    units: Units;
    timezone?: string;
    existsPrevPoint?: boolean;
    existsNextPoint?: boolean;
    selectPrevPoint?: () => void;
    selectNextPoint?: () => void;
};

export const PointDetails = ({
    gotoStreet,
    point,
    units,
    timezone = 'EST',
    existsPrevPoint,
    existsNextPoint,
    selectPrevPoint,
    selectNextPoint
}: iPointDetails) => {
    const { address = false, posted_speed, time, msg, alertActivity, device, alarmId } = point;
    const [loading, setLoading] = useState(false);
    const [isDownloadingInProcess, setIsDownloadingInProcess] = useState(false);
    const [isDownloadStarted, setIsDownloadStarted] = useState(false);
    const [isFailureWhileDownloadingPoint, setIsFailureWhileDownloadingPoint] = useState(false);
    const [isFailureWhileDownloadingEvent, setIsFailureWhileDownloadingEvent] = useState(false);
    const [isFailure, setIsFailure] = useState(false);
    const [failurePointId, setFailurePointId] = useState<string | undefined>();
    const [isAddAlarmIdPromptModalOpen, setIsAddAlarmIdPromptModalOpen] = useState(false);
    const [isAddAlarmIdFormModalOpen, setIsAddAlarmIdFormModalOpen] = useState(false);
    const [IsUserHasNotEnteredAllData, setIsUserHasNotEnteredAllData] = useState(false);
    const [makeModel, setMakeModel] = useState<string>('');
    const [imagePath, setImagePath] = useState<string>('assets/incidents/gps_fixed.svg');
    const [statusMsg, setStatusMsg] = useState<string>('');
    const [isNotification, setIsNotification] = useState<boolean>(false);
    const [showDeviceImage, setShowDeviceImage] = useState<boolean>(false);
    const [formData, setFormData] = useState({
        alarmId: alarmId,
        pointId: point.pointId,
        deviceId: point.device,
        time: point.time,
        channel: '',
        file_type: '1',
        stream_type: '2',
        before: '',
        after: ''
    });

    const dispatch = useDispatch();
    const history = useHistory();
    const selectedPoint = useSelector<iFullStoreState, IReducerSelectedPoint>(state => state.selectedPoint);
    const [isButtonHidden, setIsButtonHidden] = useState(point.hidden);

    const deviceData = useSelector<iFullStoreState, iDeviceDetails>(
        (state) => state.devicesData.devicesDetails.get(device)
    );

    type ExtraInfoMap = Record<string, unknown>;

    const { isCamera, id: deviceId } = deviceData;
    const serialNumber = (deviceData as { 'extra-info'?: ExtraInfoMap })['extra-info'];
    const serial = serialNumber && Object.values(serialNumber)[0]?.toString();
    const isEvent = !!Object.keys(point.alertActivity).length;

    const auth = useRedux(s => s.auth);
    const {
        user: { acl: user },
        isDriver
    } = auth;

    const driverId = deviceData.assignedTo?.personId;
    const assignedDriver = useSelector<iFullStoreState, iPerson>(
        state => (driverId ? state.general.people[driverId] : null)
    );

    const checkingStatus = async () => {
        setLoading(true);
        const { onlineStatus, channels, cameraTime } = await checkStatusCamera({ firebaseDeviceId: deviceData.id });

        dispatch(statusCameraOnline(onlineStatus));
        dispatch(cameraAvailableTime(cameraTime?.available_time));
        setLoading(false);
    };

    const { cameraOnline, deviceInfo, availableTime } = useSelector((state: iReducersState) => state.videoCameras);
    const CAMERA_ONLINE = cameraOnline == 1;
    const cameraStatus = CAMERA_ONLINE ? 'Camera is online' : 'Camera is offline';

    useEffect(() => {
        setImagePath('assets/incidents/gps_fixed.svg');
        getImagePath();
        loadMakeModel();
    }, [point.pointId, deviceId]);

    useEffect(() => {
        setIsButtonHidden(point.hidden);
        const failurePoints = JSON.parse(sessionStorage.getItem('failurePoints') || '[]');
        const isCurrentPointFailure = failurePoints.includes(point.pointId);

        if (!isEvent) {
            setFormData(prev => ({
                ...prev,
                stream_type: '0'
            }));
        }

        if (failurePointId && failurePointId !== point.pointId) {
            setIsFailure(false);
        } else if (isCurrentPointFailure) {
            setIsFailure(true);
            setIsNotification(false);
            setStatusMsg('An error occurred');
        }

        setIsFailureWhileDownloadingEvent(false);
        setIsFailureWhileDownloadingPoint(false);
    }, [point]);

    const hidePointHandle = async () => {
        setIsButtonHidden(true);
        const pointsRef = clientStore().collection('points').doc(point.pointId);
        await pointsRef.update({ hidden: true });

        await dispatch(hidePoint(selectedPoint.deviceId, point.tripId, point.pointId));
    };

    const showPointHandle = async () => {
        setIsButtonHidden(false);
        const pointsRef = clientStore().collection('points').doc(point.pointId);
        await pointsRef.update({ hidden: FieldValue.delete() });

        await dispatch(showPoint(selectedPoint.deviceId, point.tripId, point.pointId));
    };

    const showAlertTitle = Object.keys(alertActivity).length > 0;

    const timezoneData = getTimezoneByValue(timezone);
    const clearedTimezone = clearTimezone(timezone);
    const [timezoneTime, ...countries] = timezoneData.split(' ');
    const offSetTime = countDateWithUserAndTimezoneOffset(time, clearedTimezone);
    const alertDate = moment(time).format('YYYY-MM-DD');
    const alertTime = moment(time).format('HH:mm:ss');

    const checkingDateAndSetAvailableVideo = async () => {
        setLoading(true);
        const data = await dayChooseByTimestamp({ firebaseDeviceId: deviceId, convertDate: alertDate, alertTime });
        if (data?.length) {
            dispatch(takeDeviceInfo({ id: deviceId, alertDate, alertTime }));
            dispatch(openModal({ key: 'openHistoryModal', value: true }));
        }
        setLoading(false);
        return data;
    };

    const check30DaysLeft = isMoreThan30DaysAgo(new Date(offSetTime.format('YYYY-MM-DD')));

    useEffect(() => {
        if (isCamera && !check30DaysLeft) {
            checkingStatus();
        }
    }, [alertDate, point]);

    const showAlertHistoryVideo = () => {
        if (CAMERA_ONLINE && availableTime > 0) {
            checkingDateAndSetAvailableVideo();
        }
        if (CAMERA_ONLINE && availableTime === 0) {
            dispatch(openModal({ key: 'openBuyMoreModal', value: true }));
        }
    };

    const handleLink = (link: string) => {
        (window as any).cordova.InAppBrowser.open(link, '_system', 'location=yes');
    };

    const downloadVideoFromUrl = async (url: string, name: VideoTypeName, channel: number | string = 1) => {
        try {
            const response = await axios({
                url,
                method: 'GET',
                responseType: 'blob'
            });

            if (response.status === HttpStatus.OK) {
                const urlObject = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = urlObject;
                link.setAttribute(
                    'download',
                    `${name}_${point.pointId ?? ''}_${offSetTime.format('MM.DD.YY h mm A') ?? ''}_${channel}.mp4`
                );
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (e) {
            if (name === VideoTypeNameEnum.EVENT) {
                setIsFailureWhileDownloadingEvent(true);
            } else {
                setIsFailureWhileDownloadingPoint(true);
            }

            setStatusMsg('An error occurred while downloading');
            setIsNotification(false);
        }
    };

    const downloadAlert = async (alarmId: string) => {
        const downloadedAlert = await getDownloadAlert(alarmId);

        if (downloadedAlert.success && downloadedAlert.link) {
            await downloadVideoFromUrl(downloadedAlert.link, 'Event');
        }
        if (downloadedAlert.downloading_in_process) {
            setIsDownloadingInProcess(true);
            setStatusMsg('Download started. Check back soon.');
            setIsNotification(true);
        }
        if (downloadedAlert.add_alarm) {
            setIsAddAlarmIdPromptModalOpen(true);
        }
        if (downloadedAlert.update_alarm) {
            const asArray = Object.entries(downloadedAlert.alarmTask);
            const filtered = asArray.filter(([key, value]) => value !== '');
            const alarmTaskObjWithoutFalsyValue = Object.fromEntries(filtered);

            setFormData(prev => ({ ...prev, ...alarmTaskObjWithoutFalsyValue }));
            setIsUserHasNotEnteredAllData(true);
            setIsAddAlarmIdPromptModalOpen(true);
        }
    };

    const downloadPoint = async (pointId: string) => {
        const downloadedPoint = await getDownloadPoint(pointId);

        if (downloadedPoint.status === DownloadPointResponseStatuses.COMPLETED) {
            setIsDownloadStarted(true);

            for (const res of downloadedPoint.data) {
                await downloadVideoFromUrl(res.bucket_url, 'Point', res.channel);
            }

            setIsDownloadStarted(false);
        } else if (
            downloadedPoint.status === DownloadPointResponseStatuses.WAITING ||
            downloadedPoint.status === DownloadPointResponseStatuses.DOWNLOADING
        ) {
            setIsDownloadingInProcess(true);
            setStatusMsg('Downloading in process');
            setIsNotification(true);
        } else if (
            downloadedPoint.status === DownloadPointResponseStatuses.ADD_POINT ||
            downloadedPoint.status === DownloadPointResponseStatuses.UNKNOWN
        ) {
            setIsAddAlarmIdPromptModalOpen(true);
        } else if (downloadedPoint.status === DownloadPointResponseStatuses.FAILURE) {
            setIsFailure(true);
            setFailurePointId(pointId);
            setIsNotification(false);
            setStatusMsg('An error occurred');
            const failurePoints = new Set(JSON.parse(sessionStorage.getItem('failurePoints') || '[]'));
            failurePoints.add(pointId);
            sessionStorage.setItem('failurePoints', JSON.stringify([...failurePoints]));
        }
    };

    const loadMakeModel = async () => {
        const formatted = await getFormattedMakeModel(deviceId);
        setMakeModel(formatted);
    };

    const getImagePath = async () => {
        const event = Object.keys(point.alertActivity)[0] || '';

        if (event !== '') {
            const path = await alertDotIcon(point.alertActivity);
            if (typeof path === 'string') setImagePath(path);
        }
    };

    const generateDirections = (address?: PointAddress | null) => {
        if (!address) return null;

        return (
            <div className={styles.PointDetailsButton}>
                <span>
                    {IS_MOBILE_APP ? (
                        <button
                            type="button"
                            onClick={() =>
                                handleLink(
                                    `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                                        `${address.street},${address.city}`
                                    )}`
                                )
                            }
                        >
                            Directions
                        </button>
                    ) : (
                        <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                                `${address.street},${address.city}`
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Directions
                        </a>
                    )}
                </span>
            </div>
        );
    };

    const handleTodaysHistory = () => {
        dispatch(removeReportTrips());
        dispatch({ type: 'REPORT_RESET_ALL_FILTERS' });

        const [startDate, endDate] = getStartAndEndOfDate();

        dispatch(ReportAC.TOGGLE_ITEM_FILTER('device', deviceId));
        dispatch(ReportAC.UPSERT_DATE_GROUP_IN_DATES({ startDate, endDate }));

        history.push('/reports');
    };

    const handleLast30DayHistory = () => {
        dispatch(removeReportTrips());
        dispatch({ type: 'REPORT_RESET_ALL_FILTERS' });

        const [startDate, endDate] = getLast30DayDate();

        dispatch(ReportAC.TOGGLE_ITEM_FILTER('device', deviceId));
        dispatch(ReportAC.UPSERT_DATE_GROUP_IN_DATES({ startDate, endDate }));

        history.push('/reports');
    };

    const clearStatusBar = () => {
        setStatusMsg('');
    };

    const tagsClickHandler = () => {
        history.push(`/device/${deviceId}/add/tags`);
    };

    const formattedAddress = formatAddress(address || null);

    return (
        <div className={styles.PointDetails}>
            <PointDetailsHeader
                deviceName={deviceData.name}
                deviceIconUrl={deviceData?.icon?.url}
                imagePathFallback={imagePath}
                showDeviceImage={showDeviceImage}
                setShowDeviceImage={setShowDeviceImage}
                existsPrevPoint={existsPrevPoint}
                existsNextPoint={existsNextPoint}
                selectPrevPoint={selectPrevPoint}
                selectNextPoint={selectNextPoint}
                setStatusMsg={setStatusMsg}
                isEvent={isEvent}
                msg={msg}
                formattedAddress={formattedAddress}
                isPinned={selectedPoint?.isClicked === true}
            />

            <div className={styles.PointDetailsBorder}></div>

            <PointDetailsStatusBar
                statusMsg={statusMsg}
                isNotification={isNotification}
                onClear={clearStatusBar}
            />

            <PointDetailsInfo
                isEvent={isEvent}
                formattedAddress={formattedAddress}
                dateTime={offSetTime}
                speed={point.speed}
                units={units}
                makeModel={makeModel}
                postedSpeed={posted_speed}
                driverName={assignedDriver?.displayName}
            />

            <PointDetailsActions
                gotoStreet={gotoStreet}
                onViewAsset={() => history.push(`/device/${deviceId}`)}
                onViewDriver={assignedDriver ? () => history.push(`/person/${assignedDriver.id}`) : undefined}
                showDriverButton={!!assignedDriver}
                directions={generateDirections(address || null)}
                onTags={tagsClickHandler}
                onTodayHistory={handleTodaysHistory}
                onLast30DayHistory={handleLast30DayHistory}
                isCamera={isCamera}
                check30DaysLeft={check30DaysLeft}
                cameraStatus={cameraStatus}
                showAlertHistoryVideo={showAlertHistoryVideo}
                loading={loading}
                isEvent={isEvent}
                alarmId={alarmId}
                onDownloadAlert={() => alarmId && downloadAlert(alarmId)}
                isDownloadingInProcess={isDownloadingInProcess}
                onDownloadPoint={() => downloadPoint(point.pointId)}
                isDownloadStarted={isDownloadStarted}
                isFailure={isFailure}
                canHidePoints={user.can.includes(UserCan.HIDE_POINTS)}
                isButtonHidden={isButtonHidden}
                onHidePoint={hidePointHandle}
                onShowPoint={showPointHandle}
            />

            {isAddAlarmIdPromptModalOpen && (
                <AddAlarmIdModalPrompt
                    IsUserHasNotEnteredAllData={IsUserHasNotEnteredAllData}
                    onClosePrompt={() => setIsAddAlarmIdPromptModalOpen(false)}
                    openAddAlarmIdFormModal={() => setIsAddAlarmIdFormModalOpen(true)}
                    isEvent={isEvent}
                    handleLiveStream={showAlertHistoryVideo}
                />
            )}

            {isAddAlarmIdFormModalOpen && (
                <AddAlarmIdModalForm
                    formData={formData}
                    serial={serial}
                    firebaseDeviceId={deviceId}
                    setFormData={setFormData}
                    onCloseForm={() => setIsAddAlarmIdFormModalOpen(false)}
                    isEvent={isEvent}
                />
            )}

            {showDeviceImage && (
                <ModalImage
                    closeImageHandler={() => setShowDeviceImage(false)}
                    imageSrc={deviceData?.icon?.url ?? imagePath}
                />
            )}
        </div>
    );
};